import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Share2,
  Download,
  Trophy
} from "lucide-react";
import { Button } from "../../components/ui/button";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResultStep = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);

  useEffect(() => {
    fetchCase();
  }, []);

  const fetchCase = async () => {
    try {
      const res = await axios.get(`${API}/cases/my-case`);
      setCaseData(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getResultConfig = (result) => {
    switch (result) {
      case "approved":
        return {
          icon: CheckCircle2,
          iconClass: "text-emerald-600",
          bgClass: "bg-emerald-100",
          borderClass: "border-emerald-500",
          title: "¡Felicidades! Tu visa fue APROBADA",
          description: "Tu visa B1/B2 ha sido aprobada. ¡Prepárate para tu viaje a Estados Unidos!"
        };
      case "denied":
        return {
          icon: XCircle,
          iconClass: "text-red-600",
          bgClass: "bg-red-100",
          borderClass: "border-red-500",
          title: "Tu visa no fue aprobada",
          description: "Lamentamos informarte que tu solicitud no fue aprobada en esta ocasión. No te desanimes, podemos ayudarte a preparar una segunda solicitud."
        };
      case "administrative_processing":
        return {
          icon: Clock,
          iconClass: "text-amber-600",
          bgClass: "bg-amber-100",
          borderClass: "border-amber-500",
          title: "Procesamiento Administrativo",
          description: "Tu caso está en procesamiento administrativo. Esto puede tomar algunas semanas. Te notificaremos cuando haya novedades."
        };
      default:
        return {
          icon: Clock,
          iconClass: "text-navy-600",
          bgClass: "bg-navy-100",
          borderClass: "border-navy-300",
          title: "Esperando resultado",
          description: "Tu resultado será actualizado después de tu entrevista consular."
        };
    }
  };

  const handleShare = () => {
    const text = "¡Obtuve mi visa B1/B2 con ayuda de Divídelo! Si tú también quieres aplicar, te lo recomiendo.";
    if (navigator.share) {
      navigator.share({ title: "Divídelo - Visa Aprobada", text, url: window.location.origin });
    } else {
      navigator.clipboard.writeText(`${text} ${window.location.origin}`);
      toast.success("Enlace copiado al portapapeles");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const config = getResultConfig(caseData?.final_result);
  const Icon = config.icon;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate("/dashboard")}
            className="mb-4 text-navy-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
        </motion.div>

        {/* Result Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className={`card-elevated p-8 text-center border-t-4 ${config.borderClass}`}
        >
          <div className={`w-24 h-24 ${config.bgClass} rounded-full flex items-center justify-center mx-auto mb-6`}>
            <Icon className={`w-12 h-12 ${config.iconClass}`} />
          </div>
          
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-4">
            {config.title}
          </h1>
          <p className="text-lg text-navy-600 mb-8 max-w-md mx-auto">
            {config.description}
          </p>

          {caseData?.final_result === "approved" && (
            <div className="space-y-6">
              {/* Confetti effect placeholder */}
              <div className="flex justify-center gap-4">
                <Button
                  onClick={handleShare}
                  className="bg-gold-600 hover:bg-gold-700 text-white"
                  data-testid="share-result-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartir mi logro
                </Button>
              </div>

              {/* Referral Section */}
              <div className="mt-8 p-6 bg-gradient-to-r from-gold-50 to-amber-50 rounded-xl border border-gold-200">
                <Trophy className="w-10 h-10 text-gold-600 mx-auto mb-4" />
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                  ¿Conoces a alguien que también quiera aplicar?
                </h3>
                <p className="text-navy-600 mb-4">
                  Refiere a un amigo y ambos reciben beneficios especiales.
                </p>
                <Button variant="outline" className="border-gold-500 text-gold-700 hover:bg-gold-100">
                  Referir y ganar
                </Button>
              </div>
            </div>
          )}

          {caseData?.final_result === "denied" && (
            <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
              <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                No te rindas
              </h3>
              <p className="text-navy-600 mb-4">
                Muchas personas obtienen su visa en el segundo o tercer intento. 
                Nuestro equipo puede analizar tu caso y preparar una estrategia mejorada.
              </p>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Agendar consulta
              </Button>
            </div>
          )}

          {caseData?.final_result === "administrative_processing" && (
            <div className="mt-8 p-6 bg-amber-50 rounded-xl border border-amber-200">
              <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                ¿Qué significa esto?
              </h3>
              <p className="text-navy-600">
                El procesamiento administrativo es una revisión adicional que puede tomar 
                desde unas semanas hasta varios meses. Te notificaremos inmediatamente 
                cuando tengamos una actualización.
              </p>
            </div>
          )}
        </motion.div>

        {/* Case Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 card-elevated p-6"
        >
          <h3 className="font-heading text-lg font-bold text-navy-900 mb-4">
            Resumen de tu caso
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 bg-navy-50 rounded-lg">
              <p className="text-sm text-navy-500">Tipo de visa</p>
              <p className="font-semibold text-navy-900">{caseData?.visa_type}</p>
            </div>
            <div className="p-4 bg-navy-50 rounded-lg">
              <p className="text-sm text-navy-500">Probabilidad calculada</p>
              <p className="font-semibold text-navy-900">{caseData?.probability_score}%</p>
            </div>
            <div className="p-4 bg-navy-50 rounded-lg">
              <p className="text-sm text-navy-500">Fecha de inicio</p>
              <p className="font-semibold text-navy-900">
                {new Date(caseData?.created_at).toLocaleDateString('es-ES')}
              </p>
            </div>
            <div className="p-4 bg-navy-50 rounded-lg">
              <p className="text-sm text-navy-500">Progreso total</p>
              <p className="font-semibold text-navy-900">{caseData?.progress_percentage}%</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default ResultStep;
